import React, { useEffect, useState } from 'react';
import useAnchoredRect, { clampToViewport } from '../../../hooks/useAnchoredRect';
import useOutsideClose from '../../../hooks/useOutsideClose';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import { doctorDisplayName } from '../../../constants/admission';

/**
 * SnippetPicker (DISCHARGE-TEMPLATES Phase 1b Slice 1 · FE-2) — the
 * "Use template ▾" trigger + its anchored popover for a `snippets:true`
 * narrative field (`ot_note`, `instructions` this slice — R-B1′).
 *
 * ANCHORING: reuses the SAME `useAnchoredRect`/`clampToViewport` pair the
 * prescription pad's field panels use (`LabListPicker.jsx` etc) — a
 * `position: fixed` box measured off the trigger button, never an
 * `absolute` block pushed into the page's flow below the field (standing
 * DoD #4). `useOutsideClose` closes it the same way every other pad panel
 * does: a mousedown/touchstart outside the trigger+panel pair.
 *
 * DATA: this component owns NO fetching and imports NO store — `snippets`,
 * `loading`, `error` and `ownerDoctorName` are handed down as props by the
 * mounting parent (FE-6/FE-7, reading FE-1's `useDischargeSnippetStore`).
 * `onOpen`, if given, is called ONCE per closed→open transition and is
 * gated on `!loading` so re-opening (or a re-render) while a fetch is
 * already in flight never asks for a second one — the store itself is
 * single-flight per {admissionId, fieldKey} (FE-1), this is just "don't
 * ask again while we're already waiting".
 *
 * INSERT SEMANTICS (§2.4/build-plan FE-2): the field EMPTY (or
 * whitespace-only) ⇒ one "Use" action, direct. The field NON-EMPTY ⇒
 * "Append" (default — appends at the END with a newline separator, NEVER
 * at the caret, NEVER overwrites what's already there) and "Replace"
 * (behind `ConfirmationModal` — never a native `confirm()`). Per-row
 * delete `×` is enabled only when `snippet.can_delete` (server-computed,
 * §2.4) and always goes through `ConfirmationModal` too.
 *
 * LONG-LIST RULE (standing DoD #4): first 8 rows, then a "+n more" row
 * that reveals the rest inside the SAME scrollable popover; a search box
 * appears once there are more than 8 snippets and filters title AND body,
 * case-insensitive substring, with a live "Showing N of M" count.
 *
 * Props:
 *   fieldKey         canonical catalogue key (e.g. "ot_note") — opaque,
 *                     used only for element ids/keys, never dispatched on
 *   fieldLabel       the field's human display label (`field.label`) — used
 *                     ONLY in the Replace-confirm sentence (copy-ratified-
 *                     slice1.md §A #A7); falls back to `fieldKey` if a
 *                     caller omits it (forward-compat, never a crash)
 *   value            the field's current ON-SCREEN text (decides Use vs
 *                     Append/Replace)
 *   onChange         (nextText) => void — parent applies it to the field
 *   ownerDoctorName  §2.4 `owner_doctor_name` (may be null while loading)
 *   snippets         §2.4 `snippets[]` — { snippet_id, field_key, title,
 *                     text, created_by_name, created_at, can_delete }
 *   loading          bool
 *   error            string|null — ALREADY mapped to ratified copy by the
 *                     store (bk-1177: never the server's raw sentence)
 *   onDelete         (snippetId) => Promise|void
 *   onOpen           optional () => void, fired on closed→open, gated on
 *                     `!loading`
 */

const VISIBLE_ROW_CAP = 8;
const PREVIEW_MAX_CHARS = 60;

// Ratified copy (copy-ratified-slice1.md §A #9-#11, A1-A8), business
// stakeholder verdict 2026-09-17.
export const SNIPPET_PICKER_TRIGGER_COPY = 'Use template ▾';
export const SNIPPET_PICKER_EMPTY_COPY = 'No templates saved for this field yet.';
export const SNIPPET_PICKER_LOADING_COPY = 'Loading templates…';
export const SNIPPET_PICKER_SEARCH_PLACEHOLDER = 'Search templates…';
export const SNIPPET_PICKER_USE_LABEL = 'Use';
export const SNIPPET_PICKER_APPEND_LABEL = 'Append';
export const SNIPPET_PICKER_REPLACE_LABEL = 'Replace';

/** §A #A5. */
export function snippetPickerDeleteDisabledReason(ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  return dn
    ? `Only ${dn} or the person who saved this template can delete it.`
    : 'Only the treating doctor or the person who saved this template can delete it.';
}

/** §A #A7. */
function replaceConfirmMessage(fieldLabel, title) {
  return `Replace everything typed in ${fieldLabel} with "${title}"? This cannot be undone.`;
}

/** §A #A8. */
function deleteTemplateConfirmMessage(title, ownerDoctorName) {
  const dn = doctorDisplayName(ownerDoctorName);
  const possessive = dn ? `${dn}'s` : "the treating doctor's";
  return `Delete "${title}" from ${possessive} templates? This will not change the summary you are writing. It cannot be undone.`;
}

function isEmptyValue(value) {
  return !value || !String(value).trim();
}

function truncatePreview(text) {
  const raw = String(text || '');
  return raw.length > PREVIEW_MAX_CHARS ? `${raw.slice(0, PREVIEW_MAX_CHARS)}…` : raw;
}

function matchesQuery(snippet, needle) {
  const q = needle.toLowerCase();
  return (snippet.title || '').toLowerCase().includes(q) || (snippet.text || '').toLowerCase().includes(q);
}

const panelBaseCls = 'z-[1000] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg';
const rowCls = 'flex items-start gap-2 px-2.5 py-2 border-b border-slate-100 dark:border-slate-700 last:border-b-0';
const actionBtnCls = 'shrink-0 px-2 py-1 rounded-md border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-[11px] font-semibold';
const replaceBtnCls = 'shrink-0 px-2 py-1 rounded-md border border-teal-400 dark:border-teal-500 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 text-[11px] font-semibold';
const deleteBtnCls = 'shrink-0 w-5 h-5 rounded-md border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[11px] leading-none flex items-center justify-center disabled:opacity-60';

export default function SnippetPicker({
  fieldKey, fieldLabel, value, onChange, ownerDoctorName = null, snippets = [], loading = false, error = null,
  onDelete, onOpen,
}) {
  const displayLabel = fieldLabel || fieldKey;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  // { type: 'replace'|'delete', snippet } — drives the ConfirmationModal;
  // only one can be pending at a time (D5/D6).
  const [pendingAction, setPendingAction] = useState(null);

  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const close = React.useCallback(() => setOpen(false), []);
  useOutsideClose(open, [panelRef, triggerRef], close);

  const anchor = useAnchoredRect(open, triggerRef);
  const placed = open ? clampToViewport(anchor, { width: anchor?.width }) : null;
  const floating = !!placed;

  useEffect(() => {
    if (!open) { setQuery(''); setExpanded(false); }
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && !loading && onOpen) onOpen();
      return next;
    });
  };

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length > 0;
  const showSearchBox = snippets.length > VISIBLE_ROW_CAP;
  const filteredSnippets = searching ? snippets.filter((s) => matchesQuery(s, trimmedQuery)) : snippets;
  const visibleSnippets = (!searching && !expanded) ? filteredSnippets.slice(0, VISIBLE_ROW_CAP) : filteredSnippets;
  const hiddenCount = (!searching && !expanded) ? Math.max(filteredSnippets.length - VISIBLE_ROW_CAP, 0) : 0;

  const applyAndClose = (nextText) => { onChange(nextText); setOpen(false); };

  const handleUse = (snippet) => applyAndClose(snippet.text);
  const handleAppend = (snippet) => {
    const trimmed = (value || '').replace(/\s+$/, '');
    applyAndClose(trimmed ? `${trimmed}\n${snippet.text}` : snippet.text);
  };
  const handleReplaceRequest = (snippet) => setPendingAction({ type: 'replace', snippet });
  const handleDeleteRequest = (snippet) => setPendingAction({ type: 'delete', snippet });

  const handleConfirmPending = async () => {
    if (!pendingAction) return;
    const { type, snippet } = pendingAction;
    if (type === 'replace') {
      setPendingAction(null);
      applyAndClose(snippet.text);
      return;
    }
    // delete — leave the popover open so the doctor sees the row disappear
    // once the parent's store invalidation reflows `snippets`.
    try {
      await onDelete?.(snippet.snippet_id);
    } finally {
      setPendingAction(null);
    }
  };

  const dn = doctorDisplayName(ownerDoctorName);
  const headerLine = dn ? `${dn}'s templates` : 'Saved templates';

  return (
    <div className="inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggleOpen}
        data-testid={`snippet-picker-trigger-${fieldKey}`}
        className="px-2.5 py-1 rounded-md border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-100 text-[11.5px] font-semibold"
      >
        {SNIPPET_PICKER_TRIGGER_COPY}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={`${fieldKey} templates`}
          data-testid="snippet-picker-popover"
          data-floating={floating ? 'true' : 'false'}
          onKeyDown={(e) => { if (e.key === 'Escape') { close(); triggerRef.current?.focus(); } }}
          className={panelBaseCls}
          style={{
            ...(floating ? {
              position: 'fixed', top: placed.top, left: placed.left, width: Math.max(anchor?.width || 0, 320),
            } : { width: 320 }),
            maxHeight: 'min(22rem, 60vh)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="px-2.5 py-2 border-b border-slate-200 dark:border-slate-600 shrink-0">
            <div data-testid="snippet-picker-header" className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
              {headerLine}
            </div>
            {showSearchBox && (
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={SNIPPET_PICKER_SEARCH_PLACEHOLDER}
                data-testid="snippet-picker-search"
                className="mt-1.5 w-full px-2 py-1 rounded-md border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12px]"
              />
            )}
          </div>

          <div className="overflow-y-auto flex-1" data-testid="snippet-picker-list">
            {loading && (
              <div data-testid="snippet-picker-loading" className="px-2.5 py-4 text-[12px] text-slate-500 dark:text-slate-400 text-center">
                {SNIPPET_PICKER_LOADING_COPY}
              </div>
            )}
            {!loading && error && (
              <div data-testid="snippet-picker-error" className="px-2.5 py-2 text-[12px] text-red-600 dark:text-red-400">
                {error}
              </div>
            )}
            {!loading && !error && snippets.length === 0 && (
              <div data-testid="snippet-picker-empty" className="px-2.5 py-4 text-[12px] text-slate-500 dark:text-slate-400 text-center">
                {SNIPPET_PICKER_EMPTY_COPY}
              </div>
            )}
            {!loading && !error && snippets.length > 0 && (
              <>
                {searching && filteredSnippets.length === 0 && (
                  <div data-testid="snippet-picker-no-match" className="px-2.5 py-4 text-[12px] text-slate-500 dark:text-slate-400 text-center">
                    {`No templates match "${trimmedQuery}".`}
                  </div>
                )}
                {visibleSnippets.map((snippet) => {
                  const empty = isEmptyValue(value);
                  return (
                    <div key={snippet.snippet_id} className={rowCls} data-testid={`snippet-picker-row-${snippet.snippet_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100 truncate">{snippet.title}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{truncatePreview(snippet.text)}</div>
                        <div className="text-[10px] text-slate-400 dark:text-slate-500">{snippet.created_by_name || '—'}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {empty ? (
                          <button
                            type="button"
                            className={actionBtnCls}
                            data-testid={`snippet-picker-use-${snippet.snippet_id}`}
                            onClick={() => handleUse(snippet)}
                          >
                            {SNIPPET_PICKER_USE_LABEL}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={actionBtnCls}
                              data-testid={`snippet-picker-append-${snippet.snippet_id}`}
                              onClick={() => handleAppend(snippet)}
                            >
                              {SNIPPET_PICKER_APPEND_LABEL}
                            </button>
                            <button
                              type="button"
                              className={replaceBtnCls}
                              data-testid={`snippet-picker-replace-${snippet.snippet_id}`}
                              onClick={() => handleReplaceRequest(snippet)}
                            >
                              {SNIPPET_PICKER_REPLACE_LABEL}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          disabled={!snippet.can_delete}
                          title={snippet.can_delete ? undefined : snippetPickerDeleteDisabledReason(ownerDoctorName)}
                          aria-label={snippet.can_delete ? `Delete "${snippet.title}"` : snippetPickerDeleteDisabledReason(ownerDoctorName)}
                          className={deleteBtnCls}
                          data-testid={`snippet-picker-delete-${snippet.snippet_id}`}
                          onClick={() => handleDeleteRequest(snippet)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    data-testid="snippet-picker-more"
                    onClick={() => setExpanded(true)}
                    className="w-full text-center px-2.5 py-2 text-[11.5px] font-semibold text-teal-700 dark:text-teal-300 bg-slate-50 dark:bg-slate-900"
                  >
                    {`+${hiddenCount} more`}
                  </button>
                )}
                {searching && (
                  <div data-testid="snippet-picker-count" className="px-2.5 py-1.5 text-[10.5px] text-slate-400 dark:text-slate-500">
                    {`Showing ${filteredSnippets.length} of ${snippets.length}`}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={pendingAction?.type === 'replace'}
        title="Replace what is typed?"
        message={pendingAction?.type === 'replace' ? replaceConfirmMessage(displayLabel, pendingAction.snippet.title) : ''}
        ctaLabel={SNIPPET_PICKER_REPLACE_LABEL}
        cancelLabel="Cancel"
        onConfirm={handleConfirmPending}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmationModal
        isOpen={pendingAction?.type === 'delete'}
        title="Delete template?"
        message={pendingAction?.type === 'delete' ? deleteTemplateConfirmMessage(pendingAction.snippet.title, ownerDoctorName) : ''}
        ctaLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        icon="🗑"
        autoFocusCancel
        onConfirm={handleConfirmPending}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
